import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n';
import { size } from 'lodash';
import apiCubic from 'config/api-cubic';
import requester from 'utils/requester';
import { isEmailValid } from 'utils/validator';
import { loginUser, checkLoginData } from 'actions/profile';
import { backToPageThatCausedAuth } from 'actions/app';
import { hideLoader, showLoader } from 'actions/loader';
import { hideModal, showModal } from 'actions/modal';
import { MODAL_TYPES } from 'constants/modal-types';
import Keyboard from 'containers/Keyboard';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import useClipboardValue from 'hooks/useClipboardValue';
import Header from './common/Header';
import { StyledBlock, StyledKeyboardWrapper, StyledLegalText } from './StyledComponents';
import ActionsSection from './ActionsSection';
import useOnSuccessLogin from 'hooks/useOnSuccessLogin';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';

const i18nScope = 'authorizationLoginCredentialsPage';

const SECTION_NAME = 'CREDENTIALS_FORM';
const MIN_PASS_LENGTH = 4;

const FIELDS = {
  LOGIN: 'LOGIN',
  PASSWORD: 'PASSWORD',
};

const passwordValidation = (value) => size(value) >= MIN_PASS_LENGTH;

const LoginCredentialsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [login, setLogin] = useState('');
  const [isLoginValid, setIsLoginInvalid] = useState(true);
  const [password, setPassword] = useState('');
  const [isPasswordValid, setIsPasswordInvalid] = useState(true);
  const [activeField, setActiveField] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);

  const resolveSuccess = useOnSuccessLogin();

  const showKeyboard = useCallback(() => setIsKeyboardVisible(true), []);
  const hideKeyboard = useCallback(() => setIsKeyboardVisible(false), []);

  const onValueUpdate = useCallback(
    (value) => {
      if (activeField === FIELDS.LOGIN) setLogin(value);
      if (activeField === FIELDS.PASSWORD) setPassword(value);
    },
    [activeField],
  );

  const onCursorPositionUpdate = useCallback((value) => setCursorPosition(value), []);

  const focusField = useCallback(
    (name) => {
      setActiveField(name);
      setCursorPosition(name === FIELDS.LOGIN ? login.length : password.length);
      showKeyboard();
    },
    [login.length, password.length, showKeyboard],
  );

  const onSubmit = useCallback(async () => {
    const isLoginValid = size(login) > 0;
    const isPassValid = passwordValidation(password);

    setIsLoginInvalid(isLoginValid);
    setIsPasswordInvalid(isPassValid);

    if (!isLoginValid || !isPassValid) return;

    const loginType = isEmailValid(login) ? 'email' : 'contract';
    const errorModal = { returnFocusTo: `@${SECTION_NAME}` };
    const searchParams = new URLSearchParams(history.location.search);

    const onSuccess = async (user) => {
      const authToken = apiCubic.getAuthToken();
      const { contractNumber } = user;

      try {
        const response = await requester({
          method: 'post',
          url: `${apiCubic.host}${apiCubic.endpoints.checkContract}`,
          appendParams: false,
          extendHeadersWithSession: false,
          headers: { Authorization: `Basic ${authToken}` },
          data: { contractCode: contractNumber, language: INIT_UI_LANGUAGE },
        });

        const { data } = response;

        if (data?.payload?.numberOfActivePhones === 0) {
          dispatch(
            showModal(MODAL_TYPES.PHONE, {
              contractNumber,
              onModalConfirm({ phone }) {
                dispatch(hideModal());

                searchParams.append('phone', phone);
                history.push({ pathname: '/profile/authorization/phone-activation', search: searchParams.toString() });
              },
              onModalBack() {
                dispatch(hideModal());
                innerAnalyticsVolia.postEvent({
                  eventGroup: 'alternative sign in screen',
                  eventName: 'click skip for add phone',
                });
                resolveSuccess(user);
              },
            }),
          );

          return;
        }

        await resolveSuccess(user);
      } catch (error) {
        console.error(error);
        dispatch(backToPageThatCausedAuth());
      } finally {
        dispatch(checkLoginData());
      }
    };

    dispatch(showLoader());
    await dispatch(loginUser({ login, password, loginType, errorModal, onSuccess, withLoader: false }));
    //STVWL-559, SMRT-289
    //SMRT-297
    dispatch(hideLoader());
  }, [dispatch, history, login, resolveSuccess, password]);

  useClipboardValue((value) => {
    if (activeField === FIELDS.LOGIN) setLogin(value);
    if (activeField === FIELDS.PASSWORD) setPassword(value);
    onCursorPositionUpdate(value.length);
  });

  return (
    <>
      <Header title={i18n.t(`${i18nScope}.title`)} description={i18n.t(`${i18nScope}.description`)} />
      <StyledBlock>
        <ActionsSection sectionName={SECTION_NAME} defaultSection>
          <ActionsSection.Input
            value={login}
            type="dark"
            align="center"
            cursor={isKeyboardVisible && activeField === FIELDS.LOGIN}
            cursorPosition={cursorPosition}
            placeholder={i18n.t(`${i18nScope}.loginPlaceholder`)}
            isValid={!isKeyboardVisible ? isLoginValid : true}
            invalidMessage={i18n.t(`${i18nScope}.loginInvalid`)}
            onEnter={() => focusField(FIELDS.LOGIN)}
          />
          <ActionsSection.Input
            value={'*'.repeat(password.length)}
            type="dark"
            align="center"
            cursor={isKeyboardVisible && activeField === FIELDS.PASSWORD}
            cursorPosition={cursorPosition}
            placeholder={i18n.t(`${i18nScope}.passwordPlaceholder`)}
            isValid={isPasswordValid}
            invalidMessage={i18n.t(`${i18nScope}.passwordInvalid`)}
            onEnter={() => focusField(FIELDS.PASSWORD)}
          />
          <ActionsSection.Button label={i18n.t(`${i18nScope}.next`)} onEnter={onSubmit} />
        </ActionsSection>
        <StyledBlock>
          <ActionsSection>
            <ActionsSection.Link
              label={i18n.t(`${i18nScope}.restorePassword`)}
              to="/profile/authorization/restore/password"
              analyticsEventGroup={'alternative sign in screen'}
              analyticsEventName={'click credentials recovery'}
            />
          </ActionsSection>
        </StyledBlock>
        {isKeyboardVisible && (
          <StyledKeyboardWrapper>
            <Keyboard
              onQueryUpdated={onValueUpdate}
              onCursorPositionUpdate={onCursorPositionUpdate}
              onLeave={hideKeyboard}
              query={activeField === FIELDS.LOGIN ? login : password}
              maxQuerySize={130}
              selectedLanguage="en"
              type="email"
            />
          </StyledKeyboardWrapper>
        )}
      </StyledBlock>
      <StyledLegalText>{i18n.t(`${i18nScope}.termsWarning`)}</StyledLegalText>
    </>
  );
};

export default LoginCredentialsPage;
